import { ValidatorFn } from '@angular/forms';

import { Account } from '../client/integrations-client.module';
import { Placement } from '../misc/types';
import { DynamicFormAdditionalGroups } from './inputs/dynamic-form/dynamic-form-additional-groups/dynamic-form-additional-groups';
import {
  DynamicDivider,
  DynamicFormGroup,
  DynamicFormLoader,
  DynamicInfo,
  DynamicLabel,
  DynamicMessage,
} from './inputs/dynamic-form/dynamic-form-group/dynamic-form-group';
import { DynamicFormInput } from './inputs/dynamic-form/dynamic-form-input/dynamic-form-input';
import { DynamicListInfo } from './inputs/dynamic-list-info/dynamic-list-info.component';
import { DynamicSubdomainInput } from './inputs/subdomain-input-adapter/dynamic-subdomain-input.component';

export { Candidate, Location, Offer, Placement } from '../misc/types';

export type DynamicText = DynamicTextTranslation | DynamicTextRaw;
export type DynamicTextTranslation = { key: string; variables?: { [key: string]: string } };
export type DynamicTextRaw = { text: string };

export type ValidationPattern = { errorMessage: string; format: string };

export type SelectOptionT = { label: DynamicText | string; value: string };
export type SelectOptionsT = SelectOptionT[];

export enum DynamicInputConfigType {
  TEXT = 'text',
  EMAIL = 'email',
  CHECKBOX = 'checkbox',
  CANDIDATE_EMAIL = 'candidate_email',
  TYPEAHEAD = 'typeahead',
  SELECT = 'select',
  DATE_PICKER = 'date_picker',
  DATE_DROPDOWNS = 'date_dropdowns',
  TIME_PICKER = 'time_picker',
  ADMINS = 'admins',
  ADMIN_SELECT = 'admin_select',
  CANDIDATE_PLACEMENT = 'candidate_placement',
  RESOURCE_SELECT = 'resource_select',
  GROUP = 'group',
  ADDITIONAL_GROUPS = 'additional_groups',
  MESSAGE = 'message',
  INFO = 'info',
  LABEL = 'label',
  DIVIDER = 'divider',
  GENERATED_PASSWORD = 'generated_password',
  FORM_LOADER = 'form_loader',
  COUNTRY_SELECT = 'country_select',
  REGION_SELECT = 'region_select',
  LIST_INFO = 'list_info',
  SUBDOMAIN = 'subdomain',
  PREPEND = 'prepend',
  CUSTOM = 'custom',
}

export type DynamicCommonInputConfig<Type extends DynamicInputConfigType, Value> = {
  type: Type;
  value: Value;
  name: string;
  required?: boolean;
  validators?: ValidatorFn[];
  label?: DynamicText;
  widthPercent?: number;
  helpTooltip?: DynamicText;
  hideIfEmpty?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  removable?: boolean;
};
export type AnyDynamicCommonInputConfig = DynamicCommonInputConfig<DynamicInputConfigType, any>;

export type DynamicTextInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.TEXT,
  string
> & {
  validationPattern?: ValidationPattern;
  emitEventOnEnter?: boolean;
  placeholder?: string;
};

export type DynamicSubdomainInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.SUBDOMAIN,
  string
> & {
  suffix: string;
  prefix: string;
};

export type DynamicPrependInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.PREPEND,
  string
> & {
  prepend: string;
  placeholder?: string;
};

export type DynamicEmailInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.EMAIL,
  string
> & {
  emitEventOnEnter?: boolean;
  placeholder?: string;
};

export type DynamicCheckboxInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.CHECKBOX,
  boolean
> & {
  checkboxLabel?: DynamicText;
};

export type DynamicSelectInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.SELECT,
  string
> & {
  options: SelectOptionsT;
  hasSearch?: boolean;
  iconEmptyLabel?: boolean;
};

export type DynamicCandidateEmailsInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.CANDIDATE_EMAIL,
  string
> & {
  emails: string[];
};

export type DynamicDatePickerInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.DATE_PICKER,
  string
> & {
  maxDate?: string;
};

export type DynamicDateDropdownsInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.DATE_DROPDOWNS,
  string
> & {
  maxDate?: string;
};

export type DynamicTimePickerInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.TIME_PICKER,
  string
>;

export type DynamicAdminsInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.ADMINS,
  Array<Account['admin']>
> & {
  preventCurrentAdminRemove: boolean;
  maxSelectedAdmins?: number;
};

export type DynamicPlacementInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.CANDIDATE_PLACEMENT,
  number
> & {
  placements: Placement[];
};

export type DynamicResourceSelectInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.RESOURCE_SELECT,
  any
> & {
  resource: string;
  hasSearch?: boolean;
  loadOnSelect?: boolean;
  multiple?: boolean;
  disableWhenLoading?: boolean;
};

export type DynamicCountrySelectInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.COUNTRY_SELECT,
  string
>;

export type DynamicRegionSelectInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.REGION_SELECT,
  string
>;

export type DynamicAdminSelectInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.ADMIN_SELECT,
  number
> & {
  hasSearch?: boolean;
};

export type DynamicTypeaheadInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.TYPEAHEAD,
  string
> & {
  options: string[];
};

export type DynamicGroupConfig = {
  type: DynamicInputConfigType.GROUP;
  name: string;
  inputs: DynamicInputConfig[];
  label?: DynamicText;
  helpTooltip?: DynamicText;
  removable?: boolean;
  checksum?: string;
};

export type DynamicAdditionalGroupsConfig = {
  type: DynamicInputConfigType.ADDITIONAL_GROUPS;
  name: string;
  label: DynamicText;
  options: SelectOptionsT;
  groups: DynamicGroupConfig[];
};

export type DynamicMessageConfig = {
  type: DynamicInputConfigType.MESSAGE;
  text: DynamicText;
  hidden?: boolean;
};

export type DynamicInfoConfig = {
  type: DynamicInputConfigType.INFO;
  text: DynamicText;
  hidden?: boolean;
};

export type DynamicLabelConfig = {
  type: DynamicInputConfigType.LABEL;
  text: DynamicText;
  hidden?: boolean;
};

export type DynamicDividerConfig = {
  type: DynamicInputConfigType.DIVIDER;
  hidden?: boolean;
};

export type DynamicListInfoConfig = {
  type: DynamicInputConfigType.LIST_INFO;
  header: DynamicText;
  items: DynamicText[];
};

export type DynamicFormLoaderConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.FORM_LOADER,
  string
>;

export type DynamicGeneratedPasswordConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.GENERATED_PASSWORD,
  string
>;

export type DynamicCustomInputConfig = DynamicCommonInputConfig<
  DynamicInputConfigType.CUSTOM,
  string
> & {
  component: string;
  componentData: object;
};

export type DynamicInputConfig =
  | DynamicTextInputConfig
  | DynamicEmailInputConfig
  | DynamicCheckboxInputConfig
  | DynamicTypeaheadInputConfig
  | DynamicSelectInputConfig
  | DynamicCandidateEmailsInputConfig
  | DynamicDatePickerInputConfig
  | DynamicDateDropdownsInputConfig
  | DynamicTimePickerInputConfig
  | DynamicAdminsInputConfig
  | DynamicPlacementInputConfig
  | DynamicResourceSelectInputConfig
  | DynamicGroupConfig
  | DynamicAdditionalGroupsConfig
  | DynamicMessageConfig
  | DynamicInfoConfig
  | DynamicLabelConfig
  | DynamicDividerConfig
  | DynamicAdminSelectInputConfig
  | DynamicGeneratedPasswordConfig
  | DynamicFormLoaderConfig
  | DynamicCountrySelectInputConfig
  | DynamicRegionSelectInputConfig
  | DynamicListInfoConfig
  | DynamicCustomInputConfig
  | DynamicSubdomainInputConfig
  | DynamicPrependInputConfig;

export type DynamicFormConfig = DynamicInputConfig[];

export type AnyDynamicInput =
  | DynamicFormInput
  | DynamicFormGroup
  | DynamicFormAdditionalGroups
  | DynamicMessage
  | DynamicInfo
  | DynamicLabel
  | DynamicDivider
  | DynamicFormLoader
  | DynamicListInfo
  | DynamicSubdomainInput;

export const findInputConfigIndex = <T extends DynamicInputConfigType>(
  type: T,
  configs: DynamicInputConfig[],
): number => <any>configs.findIndex(config => config.type === type);

export type Dialog = { widthPx: number; title: any; subtitle: any; form: any[] };
